import { Box, Typography } from '@mui/material'
import { Section } from '../section'
import { icons } from '../../../../../../utils/icons'
import { useTranslation } from 'react-i18next'
import { NoResponse } from '../../../../../../components/errors/noResponse'
import { DetailCreation, DetailQuestion, Answer } from '../../../../../../service/actionPlans/actionPlanById'
import { CustomPaper } from '../../../../../../components/CustomPaper'
import { useStyles } from './styles'
import { QuestionOptionType } from '../../../../../../utils/enums/optionType'
import { CatalogAnswerDetail, CatalogAnswerBadge } from '../catalogAnswerDetail'
import { getQuestionData } from './utils'

export type QuestionsSectionProps = {
  detailCreation?: DetailCreation
  detailQuestions?: DetailQuestion[]
  scrollable?: boolean
  isManualModule?: boolean
}

function QuestionsSection({ detailCreation, detailQuestions, scrollable, isManualModule }: QuestionsSectionProps) {
  const { t } = useTranslation()
  const styles = useStyles()
  const { questionText, answers, typeQuestionId, questionnaire, sectionName } = getQuestionData(detailCreation, detailQuestions)
  const hasData = !!questionText || answers.some((answer) => answer.selected)

  return (
    <Section
      icon={icons('paper')}
      title={t('actionPlan.sections.questions')}
      subtitle={questionnaire ? t('actionPlan.sections.questionnaireTitle', { questionnaire }) : undefined}
      scrollable={scrollable}
      minHeaderHeight={67}
    >
      <Box sx={styles.container}>
        {hasData && (
          <QuestionCard
            questionText={questionText}
            answers={answers}
            typeQuestionId={typeQuestionId}
            isManualModule={isManualModule}
            sectionName={sectionName}
          />
        )}

        {!hasData && (
          <NoResponse label={t('reportFolio.detail.general.noInformation')} />
        )}
      </Box>
    </Section>
  )
}

function QuestionCard({
  questionText,
  answers,
  typeQuestionId,
  isManualModule,
  sectionName,
}: {
  questionText: string
  answers: Answer[]
  typeQuestionId?: number
  isManualModule?: boolean
  sectionName?: string
}) {
  const { t } = useTranslation()
  const styles = useStyles()
  const selectedAnswersText = answers
    .filter((answer) => answer.selected)
    .map((answer) => answer.title)
    .join(' / ')

  return (
    <CustomPaper elevation={1} sx={styles.card}>
      {!!questionText && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
          <Typography sx={styles.questionText}>{questionText}</Typography>
          {!isManualModule && typeQuestionId === QuestionOptionType.CATALOG && !!answers.find(a => a.selected)?.fullAnswer?.length && (
            <CatalogAnswerBadge count={answers.find(a => a.selected)!.fullAnswer!.length} />
          )}
        </Box>
      )}

      {isManualModule ? (
        <Typography sx={styles.answerText}>{sectionName}</Typography>
      ) : typeQuestionId === QuestionOptionType.CATALOG ? (
        <CatalogAnswerDetail fullAnswer={answers.find((answer) => answer.selected)?.fullAnswer} showPathLabel={false} />
      ) : (
        <Typography sx={styles.answerText}>
          {t('actionPlan.answer')}: {selectedAnswersText}
        </Typography>
      )}
    </CustomPaper>
  )
}

export { QuestionsSection }
